import * as R from "ramda";
import * as Dfns from "date-fns/fp";
import { AVAILS, PlanRow, TYPES_MAP } from "@blisspointmedia/bpm-types/dist/LinearBuying";
import { convert24hrTo12hr } from "./time-utils";

export const DATE_FORMAT = "yyyy-MM-dd";
const DAYS_OF_WEEK = ["M", "Tu", "W", "Th", "F", "Sa", "Su"] as const;
// The estimated audience size of a Local airing compared to its National counterpart.
const LOCAL_AUDIENCE_CORRECTION_FACTOR = 0.373;

export const DEMO_GROUPINGS = {
  Meta: {
    HH: "hh",
    "M18+": "m18plus",
    "W18+": "w18plus",
    "A18+": "a18plus",
    "A18-34": "a18_34",
    "A18-49": "a18_49",
    "A25-54": "a25_54",
    "A35-54": "a35_54",
    "A55+": "a55plus",
  },
  Men: {
    "18 - 20": "m18_20",
    "18 - 34": "m18_34",
    "18 - 49": "m18_49",
    "21 - 24": "m21_24",
    "25 - 29": "m25_29",
    "25 - 54": "m25_54",
    "30 - 34": "m30_34",
    "35 - 39": "m35_39",
    "35 - 54": "m35_54",
    "40 - 44": "m40_44",
    "45 - 49": "m45_49",
    "50 - 54": "m50_54",
    "55 - 64": "m55_64",
    "55+": "m55plus",
    "65+": "m65plus",
  },
  Women: {
    "18 - 20": "w18_20",
    "18 - 34": "w18_34",
    "18 - 49": "w18_49",
    "21 - 24": "w21_24",
    "25 - 29": "w25_29",
    "25 - 54": "w25_54",
    "30 - 34": "w30_34",
    "35 - 39": "w35_39",
    "35 - 54": "w35_54",
    "40 - 44": "w40_44",
    "45 - 49": "w45_49",
    "50 - 54": "w50_54",
    "55 - 64": "w55_64",
    "55+": "w55plus",
    "65+": "w65plus",
  },
};

export const PRETTY_DEMO_NAMES = {
  hh: "HH",
  m18plus: "M18+",
  w18plus: "W18+",
  a18plus: "A18+",
  a18_34: "A18-34",
  a18_49: "A18-49",
  a25_54: "A25-54",
  a35_54: "A35-54",
  a55plus: "A55+",
  m18_20: "M18-20",
  m18_34: "M18-34",
  m18_49: "M18-49",
  m21_24: "M21-24",
  m25_29: "M25-29",
  m25_54: "M25-54",
  m30_34: "M30-34",
  m35_39: "M35-39",
  m35_54: "M35-54",
  m40_44: "M40-44",
  m45_49: "M45-49",
  m50_54: "M50-54",
  m55_64: "M55-64",
  m55plus: "M55+",
  m65plus: "M65+",
  w18_20: "W18-20",
  w18_34: "W18-34",
  w18_49: "W18-49",
  w21_24: "W21-24",
  w25_29: "W25-29",
  w25_54: "W25-54",
  w30_34: "W30-34",
  w35_39: "W35-39",
  w35_54: "W35-54",
  w40_44: "W40-44",
  w45_49: "W45-49",
  w50_54: "W50-54",
  w55_64: "W55-64",
  w55plus: "W55+",
  w65plus: "W65+",
};

export const OVERLAPPING_DEMO_LOOKUP = {};

for (let [key, val] of Object.entries(DEMO_GROUPINGS.Men)) {
  PRETTY_DEMO_NAMES[val] = `M${key}`.replace(/ /g, "");
  OVERLAPPING_DEMO_LOOKUP[val] = [];
}
for (let [key, val] of Object.entries(DEMO_GROUPINGS.Women)) {
  PRETTY_DEMO_NAMES[val] = `W${key}`.replace(/ /g, "");
  OVERLAPPING_DEMO_LOOKUP[val] = [];
}

interface AudienceEstimateByTimeBlock {
  [halfHourBlock: string]: number;
}

export interface NielsenEstimates {
  [network: string]: AudienceEstimateByTimeBlock;
}

export interface AudienceInfo {
  avail: typeof AVAILS[number];
  network: string;
  dow: string[];
  daypart_start: string;
  daypart_end: string;
  type: string;
  cost: number;
  count: number;
}

interface CalculateAudienceInfo {
  row: PlanRow | AudienceInfo;
  nielsenEstimates: NielsenEstimates;
  universeEstimate?: number;
  assumedClearanceRate?: number | undefined;
}

/**
 * Corrects the format of a time in 24hr format.
 * Start and end times that come from rotations and pricing don't have a leading 0 for times before 10:00,
 * which doesn't follow the 24hr time standard, and messes up the time input.
 * e.g. 9:00 -> 09:00
 */
export const time24hr = (daypart: string): string => {
  if (!daypart) {
    return "";
  }
  let time = R.pipe(Dfns.parse(new Date(), "H:mm"), Dfns.format("HH:mm"))(daypart);
  return time;
};

/**
 * Calculates impressions, CPM, and TRP for a plan row.
 */
export const calculateAudienceInfo = ({
  row,
  nielsenEstimates,
  universeEstimate,
  assumedClearanceRate,
}: CalculateAudienceInfo): { impressions: number; cpm: number; trp: number } => {
  const { avail, network, dow, daypart_start, daypart_end, type } = row;
  const spend = row.cost * row.count;
  const daysMap = dow.reduce((prev, curr) => ({ ...prev, [curr]: true }), {});

  // Check if the rotation spans across 2 days (past midnight).
  let rotationOverlapsDays = false;
  if (daypart_end < daypart_start) {
    rotationOverlapsDays = true;
  }

  let totalAudience = 0;
  let timeBlocks = 0;
  for (let day of R.keys(daysMap)) {
    const networkDayEstimates: Record<string, number> | undefined = R.path(
      [network, day],
      nielsenEstimates
    );
    for (let time of R.keys(networkDayEstimates)) {
      if (!rotationOverlapsDays && time >= daypart_start && time <= daypart_end) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === "L") {
          audience = audience * LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      } else if (rotationOverlapsDays && time >= daypart_start) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === "L") {
          audience = audience * LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      } else if (rotationOverlapsDays && time <= daypart_end) {
        let audience = R.pathOr(0, [time], networkDayEstimates);
        if (avail === "L") {
          audience = audience * LOCAL_AUDIENCE_CORRECTION_FACTOR;
        }
        totalAudience += audience;
        timeBlocks++;
      }
    }
  }

  const averageAudience = totalAudience / timeBlocks;
  let impressions = averageAudience * row.count;
  let cpm = (spend / impressions) * 1000;
  if (cpm === Infinity) {
    cpm = 0;
  }

  if (assumedClearanceRate && type !== TYPES_MAP.Secured) {
    impressions = impressions * assumedClearanceRate;
  }

  let trp;
  if (universeEstimate) {
    trp = (100 * (impressions / 1000)) / universeEstimate;
    if (assumedClearanceRate) {
      trp *= assumedClearanceRate;
    }
  }
  return { impressions: Math.round(impressions || 0), cpm: cpm || 0, trp: trp || 0 };
};

/**
 * Convert days of week from v2 to v1 format
 */
export const convertDaysOfWeekToExcelFormat = (dow: string[]): string => {
  let convertedDow = "";

  for (let day of DAYS_OF_WEEK) {
    if (dow.includes(day)) {
      convertedDow += day;
    } else if (!dow.includes(day) && (day === "M" || day === "W" || day === "F")) {
      convertedDow += "-";
    } else {
      convertedDow += "--";
    }
  }
  return convertedDow;
};

/**
 * Convert daypart from v2 to v1 format.
 * In v1, daypart start and end times are one string, and in 12hr time format.
 */
export const convertDaypartToExcelFormat = (daypart_start: string, daypart_end: string): string => {
  return `${convert24hrTo12hr(daypart_start)}-${convert24hrTo12hr(daypart_end)}`;
};
